import React, { useState, useEffect } from 'react';
import { Sparkles, CheckCircle2, XCircle, Loader2 } from 'lucide-react';
import type { ChatStep } from '../../../types/stream.types';
import type { ToolContent } from './tool-renderer.types';
import { getToolRenderer } from './tool-registry';
import { ThoughtStepRenderer } from './thought-step-renderer';
import { StepLayout } from './step-layout';
import { MarkdownMessage } from '../../molecules/markdown/MarkdownMessage';

interface StepItemProps {
  step: ChatStep;
  isLast: boolean;
  isStreaming: boolean;
  nestedInActivityGroup?: boolean;
  onViewDocument?: (url: string, name: string) => void;
}

export function StepItem({
  step,
  isLast,
  isStreaming,
  nestedInActivityGroup = false,
  onViewDocument,
}: StepItemProps) {
  const [isExpanded, setIsExpanded] = useState(step.type === 'text_output');
  const isThought = step.type === 'thought';
  const isToolCall = step.type === 'tool_call';
  const isTextOutput = step.type === 'text_output';
  const hasOutput = step.output !== undefined;
  const isSuccess = step.output?.success !== false;

  const rawToolName = (step.metadata?.tool_name as string) || step.agent || 'Tool';
  const toolName = rawToolName.replace(/_/g, ' ');

  useEffect(() => {}, [step.output, step.message]);

  const toolRenderer = isToolCall ? getToolRenderer(step) : null;
  const thoughtRenderer = isThought ? new ThoughtStepRenderer() : null;

  const hasContent = isThought
    ? !!(step.message && step.message.trim().length > 0)
    : isTextOutput
      ? !!(step.message && step.message.trim().length > 0)
      : !!(hasOutput && step.output?.content);

  const summary = isThought
    ? thoughtRenderer?.getSummary(step, isStreaming)
    : isTextOutput
      ? { text: (step.message || '').substring(0, 60).trim() || 'Response' }
      : toolRenderer?.getSummary(step, isStreaming);

  const content: ToolContent | undefined = isThought
    ? thoughtRenderer?.getContent(step)
    : isTextOutput
      ? {
          main: (
            <MarkdownMessage
              content={step.message || ''}
              isStreaming={isStreaming && isLast}
              fontSize="text-[14px] md:text-[15px]"
              textColor="text-slate-800"
              onViewDocument={onViewDocument}
            />
          ),
        }
      : toolRenderer?.getContent(step);

  const renderIcon = () => {
    if (isThought) return <Sparkles size={10} className="text-gray-400" strokeWidth={2} />;
    if (!hasOutput) return <Loader2 size={10} className="text-blue-500 animate-spin" strokeWidth={2.5} />;
    if (isSuccess) return <CheckCircle2 size={10} className="text-emerald-500" strokeWidth={2.5} />;
    return <XCircle size={10} className="text-red-500" strokeWidth={2.5} />;
  };

  const title = (
    <span
      className={
        isThought          ? 'text-gray-500'
          : isTextOutput
            ? 'text-primary/80'
            : 'text-gray-700 font-medium capitalize'
      }
    >
      {isThought ? 'Reasoning' : isTextOutput ? 'Response' : toolName}
    </span>
  );

  if (isTextOutput) {
    return (
      <div className="text-slate-800 text-[14px] md:text-[15px] leading-relaxed overflow-x-hidden break-words w-full">
        {content?.main}
      </div>
    );
  }

  return (
    <StepLayout
      icon={renderIcon()}
      title={title}
      summary={summary?.text}
      hasContent={!!hasContent}
      isExpanded={isExpanded}
      onToggle={() => setIsExpanded(!isExpanded)}
      isStreaming={isStreaming && !hasOutput && !isTextOutput}
      nested={nestedInActivityGroup}
    >
      {content?.main}
      {content?.metadata && (
        <div className="mt-2 pt-2 border-t border-gray-50">
          {content.metadata}
        </div>
      )}
    </StepLayout>
  );
}
